import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import {
  PortalDsAlert,
  portalDs,
} from 'src/components/portal-design-system';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from 'src/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/components/ui/tabs';
import { cn } from 'src/lib/utils';
import {
  isPortalSyncNotConfiguredError,
  useGetPortalWorkspaceBulkSettingsQuery,
  useGetPortalWorkspaceQuery,
} from '../endpoints/portal-users.endpoints';
import type {
  PortalWorkspaceDetail,
  PortalWorkspaceMember,
  PortalWorkspaceSummary,
} from '../types/users.types';
import { resolvePortalWorkspaceUsage } from '../utils/portal-users.mappers';
import { TeamBulkSettingsPanel, type TeamBulkSettingsDraft } from './team-bulk-settings-panel';
import { TeamMemberList } from './team-member-list';
import { UsageBreakdownCell } from './usage-breakdown-cell';

const DRAWER_TAB_TRIGGER_CLASS = cn(
  'rounded-none border-b-2 border-transparent bg-transparent px-0 pb-3 text-[14px] font-semibold text-[#5E6278] shadow-none',
  'hover:bg-transparent hover:text-[#0D0F1A]',
  '[&[aria-selected=true]]:border-[#0D0F1A] [&[aria-selected=true]]:bg-transparent [&[aria-selected=true]]:text-[#0D0F1A] [&[aria-selected=true]]:shadow-none',
  'mr-6 last:mr-0'
);

type DrawerTab = 'overview' | 'settings';

interface TeamDetailDrawerProps {
  workspaceId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  listTeam?: PortalWorkspaceSummary | null;
  actionsDisabled?: boolean;
  onMemberClick: (member: PortalWorkspaceMember) => void;
  onApplyBulkSettings: (
    workspaceId: string,
    settings: TeamBulkSettingsDraft,
  ) => Promise<{ ok: boolean; message?: string }>;
}

function buildFallbackWorkspace(
  listTeam: PortalWorkspaceSummary
): PortalWorkspaceDetail {
  return {
    ...listTeam,
    usage: resolvePortalWorkspaceUsage(listTeam),
    members: [],
  };
}

export function TeamDetailDrawer({
  workspaceId,
  open,
  onOpenChange,
  listTeam,
  actionsDisabled = false,
  onMemberClick,
  onApplyBulkSettings,
}: TeamDetailDrawerProps) {
  const [activeTab, setActiveTab] = useState<DrawerTab>('overview');

  useEffect(() => {
    if (open) {
      setActiveTab('overview');
    }
  }, [open, workspaceId]);

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useGetPortalWorkspaceQuery(workspaceId ?? '', {
    skip: !open || !workspaceId,
  });

  const {
    data: bulkSettings,
    isLoading: isBulkSettingsLoading,
    refetch: refetchBulkSettings,
  } = useGetPortalWorkspaceBulkSettingsQuery(workspaceId ?? '', {
    skip: !open || !workspaceId,
  });

  const workspace = data ?? (
    listTeam && workspaceId === listTeam.workspace_id
      ? buildFallbackWorkspace(listTeam)
      : null
  );

  const isSyncNotConfigured = isError && isPortalSyncNotConfiguredError(error);

  const handleApplyBulkSettings = async(settings: TeamBulkSettingsDraft) => {
    if (!workspaceId) {
      return { ok: false as const, message: 'Workspace not found.' };
    }

    const result = await onApplyBulkSettings(workspaceId, settings);
    if (result.ok) {
      void refetch();
      void refetchBulkSettings();
    }
    return result;
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 overflow-y-auto bg-white p-0 sm:max-w-2xl"
      >
        <div className={portalDs.sheetHeader}>
          <SheetHeader className="space-y-1 text-left">
            <SheetTitle className={portalDs.sheetTitle}>
              {workspace?.workspace_name ?? 'Team detail'}
            </SheetTitle>
            <SheetDescription className={portalDs.sheetDescription}>
              {workspace
                ? `${workspace.member_count} ${workspace.member_count === 1 ? 'member' : 'members'} · aggregated usage and team-wide settings`
                : 'Review members, usage, and team-wide settings.'}
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="flex-1 px-6 py-5">
          {isLoading ? (
            <div
              className={cn('flex items-center gap-2 py-8', portalDs.bodySm)}
              role="status"
            >
              <Loader2 className="size-4 animate-spin text-[#9CA3AF]" />
              Loading team detail…
            </div>
          ) : isSyncNotConfigured ? (
            <PortalDsAlert
              variant="warning"
              title="Team management is unavailable"
            >
              Portal sync is not configured. Team detail cannot be loaded.
            </PortalDsAlert>
          ) : isError ? (
            <PortalDsAlert
              variant="danger"
              title="Could not load team detail"
            >
              Team data is temporarily unavailable. Try again shortly.
            </PortalDsAlert>
          ) : workspace ? (
            <Tabs
              value={activeTab}
              onValueChange={(value) => setActiveTab(value as DrawerTab)}
            >
              <TabsList className="mb-6 h-auto w-full justify-start rounded-none border-b border-[#EDEEF2] bg-transparent p-0">
                <TabsTrigger
                  value="overview"
                  className={DRAWER_TAB_TRIGGER_CLASS}
                >
                  Overview
                </TabsTrigger>
                <TabsTrigger
                  value="settings"
                  className={DRAWER_TAB_TRIGGER_CLASS}
                >
                  Team settings
                </TabsTrigger>
              </TabsList>

              <TabsContent
                value="overview"
                className="mt-0 space-y-8"
              >
                <section className={portalDs.sheetSection}>
                  <h3 className={portalDs.sheetSectionTitle}>
                    Aggregated usage (30 days)
                  </h3>
                  <UsageBreakdownCell usage={resolvePortalWorkspaceUsage(workspace)} />
                </section>

                <section className={portalDs.sheetSection}>
                  <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                    <h3 className={portalDs.sheetSectionTitle}>
                      Members
                    </h3>
                    <span className={portalDs.rowCount}>
                      {workspace.member_count}
                      {' '}
                      {workspace.member_count === 1 ? 'member' : 'members'}
                    </span>
                  </div>

                  {isFetching && !isLoading ? (
                    <p className={portalDs.bodySm}>
                      Refreshing members…
                    </p>
                  ) : workspace.members.length === 0 ? (
                    <p className={portalDs.bodySm}>
                      No members found for this team.
                    </p>
                  ) : (
                    <TeamMemberList
                      workspace={workspace}
                      onMemberClick={onMemberClick}
                    />
                  )}
                </section>
              </TabsContent>

              <TabsContent
                value="settings"
                className="mt-0"
              >
                {isBulkSettingsLoading ? (
                  <div
                    className={cn('flex items-center gap-2 py-8', portalDs.bodySm)}
                    role="status"
                  >
                    <Loader2 className="size-4 animate-spin text-[#9CA3AF]" />
                    Loading team settings…
                  </div>
                ) : (
                  <TeamBulkSettingsPanel
                    workspaceId={workspace.workspace_id}
                    workspaceName={workspace.workspace_name}
                    memberCount={workspace.member_count}
                    currentTier={workspace.tier}
                    bulkSettings={bulkSettings}
                    disabled={actionsDisabled}
                    onApply={handleApplyBulkSettings}
                  />
                )}
              </TabsContent>
            </Tabs>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
